<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';

const props = defineProps({
  card: { type: Object, required: true },
});

const route = useRoute();
const router = useRouter();
const agents = useMapGetter('agents/getAgents');

const nowTick = ref(Date.now());
let tickHandle = null;
onMounted(() => {
  tickHandle = setInterval(() => {
    nowTick.value = Date.now();
  }, 60000);
});
onUnmounted(() => clearInterval(tickHandle));

const timeInList = computed(() => {
  // nowTick is read only to force a recompute every minute; dynamicTime always
  // measures against the real current time.
  return (
    nowTick.value &&
    dynamicTime(
      Math.floor(new Date(props.card.entered_list_at).getTime() / 1000)
    )
  );
});

const assignedAgents = computed(() =>
  (props.card.assignee_ids || [])
    .map(id => agents.value.find(agent => agent.id === id))
    .filter(Boolean)
);

const handleDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(
    'application/x-kanban-card-id',
    String(props.card.id)
  );
};

const openConversation = () => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.card.conversation.display_id,
    })
  );
  router.push({ path });
};
</script>

<template>
  <div
    class="kanban-card flex flex-col gap-2 p-3 rounded-lg bg-n-solid-1 border border-n-weak border-l-[3px] cursor-pointer hover:bg-n-solid-2"
    :class="{ 'kanban-card--overdue': card.overdue }"
    :style="{ borderLeftColor: card.conversation.inbox_color || '#64748B' }"
    draggable="true"
    :data-card-id="card.id"
    @dragstart="handleDragStart"
    @click="openConversation"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <Avatar
          :name="card.conversation.contact.name"
          :src="card.conversation.contact.avatar_url"
          :size="20"
          rounded-full
        />
        <span class="truncate text-body-main text-n-slate-12">
          {{ card.conversation.contact.name }}
        </span>
      </div>
      <CardPriorityIcon :priority="card.priority" />
    </div>

    <div
      v-if="card.external_status"
      data-test-id="kanban-card-external-status"
      class="self-start px-2 py-0.5 rounded-md bg-n-slate-3 text-xs text-n-slate-11"
    >
      {{ `[${card.external_status}]` }}
    </div>

    <div class="flex items-center justify-between text-xs text-n-slate-11">
      <span>{{ timeInList }}</span>
      <div class="flex -space-x-1">
        <Avatar
          v-for="agent in assignedAgents.slice(0, 3)"
          :key="agent.id"
          :name="agent.name"
          :src="agent.thumbnail"
          :size="18"
          rounded-full
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card--overdue {
  @apply ring-1 ring-n-ruby-9;
}
</style>
